import type { ReactNode } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { serializeJsonLd } from '../../lib/jsonLd';
import { faqPageSchema, type FaqEntry } from '../../lib/seo/faqPage';
import { Eyebrow } from '../Eyebrow';
import { Band } from './Band';
import { m } from '../../paraglide/messages';

/**
 * A page's FAQ band: the questions in an accordion plus matching FAQPage JSON-LD.
 *
 * `forceMount` is load-bearing. Radix unmounts a closed `AccordionContent`, so
 * without it every answer would be absent from the served HTML and would exist
 * only inside the structured data that claims to describe it - and no answer
 * engine runs the JavaScript that would reveal them. Mounted-and-hidden is the
 * state Google documents as fine to index.
 */
export const FaqBand = ({
  heading,
  lede,
  ledeClassName = 'max-w-[46ch]',
  questions,
}: {
  heading: ReactNode;
  lede: ReactNode;
  ledeClassName?: string;
  questions: readonly FaqEntry[];
}) => (
  <Band id="faq">
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: serializeJsonLd(faqPageSchema(questions)) }}
    />
    <div className="band grid gap-x-16 gap-y-10 lg:grid-cols-[5fr_7fr]">
      <div>
        <Eyebrow>{m.faq_band_eyebrow()}</Eyebrow>
        <h2 className="display-2 mt-4 text-text">{heading}</h2>
        <p className={`lede mt-5 ${ledeClassName}`}>{lede}</p>
      </div>

      <Accordion type="single" collapsible className="w-full border-t border-rule">
        {questions.map((question) => (
          <AccordionItem key={question.q} value={question.q} className="border-rule">
            <AccordionTrigger className="text-16 font-medium">{question.q}</AccordionTrigger>
            <AccordionContent forceMount className="max-w-[62ch] text-text-2">
              {question.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </Band>
);
